import React, { memo, useRef, useEffect, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { getFromApiAsync } from '../../helpers/apiHelper';
import { formatNumber_N1, priceToNumber } from '../../helpers/utils';

const upColor = '#089981', downColor = '#F23645', textColor = '#D6D8DA';

export default memo(function CandleStickHistChart(props) {
    const { code, type, chartType } = props;

    const {t} = useTranslation();
    const language = useSelector(state => state.gShared.language);
    const tradingDate = useSelector(state => state.gShared.tradingDate ?? '');

    const i_counter = useSelector(state => state.gIndex.indexInfos.find(item => type === 'IDX' && item.indexCode === code)?.counter);
    const indexOnline = useSelector(state => state.gIndex.indexInfos.find(item => type === 'IDX' && item.indexCode === code)) ?? {};
    const s_counter = useSelector(state => state.gSymbol.symbolInfos.find((e) => type === 'ST' && e.code === code)?.counter);
    const stockOnline = useSelector(state => state.gSymbol.symbolInfos.find((e) => type === 'ST' && e.code === code))  ?? {};

    //
    const refChart = useRef(null);
    const refFlagCheck = useRef(null);

    //
    const [chartDatas, setChartDatas] = useState([]);

    const _getSign = (open, close, prevClose) => {
        let _sign = 1;
        if(open > close) {
            _sign = -1;
        }
        else if(open < close) {
            _sign = 1;
        }
        else if(prevClose < close)  {
            _sign = 1;
        }
        else if(prevClose > close)  {
            _sign = -1;
        }
        return _sign;
    }

    //
    const _getChartOptions = () => {
        let _dataSet = [];
        let prevClose = 0;
        //
        if(chartDatas && chartDatas.length > 0) {
            chartDatas.sort((a, b) => a.time - b.time).forEach(item => {
                const date = new Date(item.time);
                const time =  date.getFullYear().toString().padStart(4, "0")  + "-" + (date.getMonth() + 1).toString().padStart(2, "0") + "-" +  date.getDate().toString().padStart(2, "0") ;
             
                let _sign = _getSign(item.open, item.close, prevClose);

                // [open, close, lowest, highest]
                const pointData = [
                    time,
                    item.open,
                    item.close,
                    item.lowest,
                    item.highest,
                    item.volume, 
                    _sign
                ];
                //
                prevClose =  item.close;
                _dataSet.push(pointData);
            });
        }
        //
        if(type === 'ST') {
            if(stockOnline.totalTradedQttyNM && stockOnline.totalTradedQttyNM > 0) {
                const match = priceToNumber(stockOnline.matchPrice) * 1000;
                const open = priceToNumber(stockOnline.openPrice) * 1000;
                const close = priceToNumber(stockOnline.closePrice) * 1000;
                if(match != 0) {
                    const pointDataInday = [
                        tradingDate,
                        open,
                        (close != 0 ? close : match),
                        priceToNumber(stockOnline.lowestPrice) * 1000,
                        priceToNumber(stockOnline.highestPrice) * 1000,
                        priceToNumber(stockOnline.totalTradedQttyNM),
                        _getSign(open, (close != 0 ? close : match), prevClose)
                    ];
                    _dataSet.push(pointDataInday);
                }
            }
        }
        else if(type === 'IDX') {
            const current = priceToNumber(indexOnline.currentIndex);
            const open = priceToNumber(indexOnline.openIndex);
            const close = priceToNumber(indexOnline.closeIndex);
            const lowest = priceToNumber(indexOnline.lowestIndex);
            const highest = priceToNumber(indexOnline.highestIndex);
            if(current != 0) {
                const pointDataInday = [
                    tradingDate,
                    (open != 0 ? open : current),
                    (close != 0 ? close : current),
                    (lowest != 0 ? lowest : current),
                    (highest != 0 ? highest : current),
                    priceToNumber(indexOnline.totalTradedQtty),
                    _getSign((open != 0 ? open : current), (close != 0 ? close : current), prevClose)
                ];
                _dataSet.push(pointDataInday);
            }
        }   
        //
        return {
            useUTC: false,
            textStyle: {
                fontFamily: "tahoma, 'Be Vietnam Pro'",
            },
            dataset: {
                source: _dataSet
            },
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'cross'
                },
                borderWidth: 1,
                padding: 10,
                confine: true,
                formatter: function(params) {
                    // [time, open, close, lowest, highest, volume, sign]
                    // console.log(params);
                    if(params && params.length > 0) {
                        const data = params[0].data;
                        const marker = params[0].marker;
                        const subMarker = '<span style="display:inline-block;margin-right:4px;margin-bottom:3px;border-radius:5px;width:5px;height:5px;width:5px;background-color:' + params[0].color + ';"></span>';
                        //
                        let rez = '<p>' + data[0] + '</p>';
                        rez += '<div class="chart-tooltip-grid-3">';
                        rez += '<div style="grid-column: 1 / span 3; padding: 3px 0"></div>';
                        rez += '<div>' + marker + '</div><div>' + t('gia') + '</div><div class="text-right"></div>';
                        rez += '<div class="text-center">' + subMarker + '</div><div>' + t('mo') + ':</div><div class="text-right"><b>' + formatNumber_N1(data[1]) + '</b></div>';
                        rez += '<div class="text-center">' + subMarker + '</div><div>' + t('dong') + ':</div><div class="text-right"><b>' + formatNumber_N1(data[2]) + '</b></div>';
                        rez += '<div class="text-center">' + subMarker + '</div><div>' + t('thap') + ':</div><div class="text-right"><b>' + formatNumber_N1(data[3]) + '</b></div>';
                        rez += '<div class="text-center">' + subMarker + '</div><div>' + t('cao') + ':</div><div class="text-right"><b>' + formatNumber_N1(data[4]) + '</b></div>';
                        rez += '<div style="grid-column: 1 / span 3; padding: 3px 0"></div>';
                        rez += '<div>' + marker + '</div><div>' + t('tongKL') + ':</div><div class="text-right"><b>' + formatNumber_N1(data[5]) + '</b></div>';
                        rez += '</div>';
                        return rez;
                    }
                },
            },
            axisPointer: {
                link: [
                    {
                        xAxisIndex: 'all'
                    }
                ],
                label: {
                    backgroundColor: '#777'
                }
            },
            visualMap: {
                show: false,
                seriesIndex: 1,
                dimension: 6,
                pieces: [
                    {
                        value: 1,
                        color: upColor,
                    },
                    {
                        value: -1,
                        color: downColor,
                    }
                ]
            },
            xAxis: [
                {
                    type: 'category',
                    demension: 0,
                    scale: true,
                    axisLabel: { color: textColor, },
                    axisLine: { onZero: false, lineStyle: { color: textColor }, },
                    axisTick: { lineStyle: { color: textColor } },
                    splitLine: { show: false },
                    min: 'dataMin',
                    max: 'dataMax',
                    axisPointer: {
                        z: 100
                    },
                },
                {
                    type: 'category',
                    demension: 0,
                    gridIndex: 1,
                    scale: true,
                    axisLabel: { show: false },
                    axisLine: { onZero: false, lineStyle: { color: textColor }, },
                    axisTick: { show: false },
                    splitLine: { show: false },
                    min: 'dataMin',
                    max: 'dataMax',
                }
            ],
            yAxis: [
                {
                    scale: true,
                    splitLine: { 
                        lineStyle: {
                            color: "#6E7079",
                            type: 'dashed',
                        }
                    },
                    axisLabel: { color: textColor, },
                    axisLine: { onZero: false, lineStyle: { color: textColor } },
                },
                {
                    scale: true,
                    gridIndex: 1,
                    splitNumber: 2,
                    axisLabel: { show: false },
                    axisLine: { show: false },
                    axisTick: { show: false },
                    splitLine: { show: false },
                    min: 0,
                }
            ],
            series: [
                {
                    name: 'Price',
                    type: 'candlestick',
                    itemStyle: {
                        color: upColor,
                        color0: downColor,
                        borderColor: upColor,
                        borderColor0: downColor,
                    },
                    encode: {
                        x: 0,
                        y: [1, 2, 3, 4],
                    },
                    barMaxWidth: 30,
                },
                {
                    name: 'Volume',
                    type: 'bar',
                    xAxisIndex: 1,
                    yAxisIndex: 1,
                    encode: {
                        x: 0,
                        y: 5,
                    },
                    barMaxWidth: 30,
                }
            ],
            grid: [{
                left: _dataSet && _dataSet.length > 0 ? '70px' : '20px',
                right: '20px',
                bottom: '30%',
            },
            {
                left: _dataSet && _dataSet.length > 0 ? '70px' : '20px',
                right: '20px',
                top: '80%',
                bottom: '20px',
            }],
        }
    }

    const _getChartData = (getCode, getType, getChartType) => {
        try {
            const _flgCheck = getCode + "|" + getType + "|" + getChartType;
            if(getType === "ST") {
                getFromApiAsync("pr/stockhist/chartdata/" + getCode + "?type=" + getChartType).then(res => {
                    if(refFlagCheck.current === _flgCheck) {
                        setChartDatas(res.data);
                    }
                });
            }
            else if(getType === "IDX") {
                getFromApiAsync("pr/indexhist/chartdata/" + getCode + "?type=" + getChartType).then(res => {
                    if(refFlagCheck.current === _flgCheck) {
                        setChartDatas(res.data);
                    }
                });
            }
        } catch (error) {
                
        }
    }

    const _handleWindowResize = () => {
        if(refChart && refChart.current)
        {
            refChart.current.getEchartsInstance().resize();
        }
    }

    useEffect(() => {
        if(refChart && refChart.current)
        {
            refChart.current.getEchartsInstance().setOption(_getChartOptions());
        }
    });

    useEffect(() => {
        refFlagCheck.current = code + "|" + type + "|" + chartType;
        _getChartData(code, type, chartType);
    }, [code, type, chartType])

    useEffect(() => {
        window.addEventListener('resize', _handleWindowResize);
        return () => {
            window.removeEventListener('resize', _handleWindowResize);
        };
    }, [])

    //
    return (
        <ReactECharts style={{width: '100%', height: '100%', ...props.style}}
            ref={refChart}
            lazyUpdate={true}
            option={_getChartOptions()}
        />
    )
}, (prevProps, nextProps) => {
    if (prevProps.code === nextProps.code 
        && prevProps.type === nextProps.type
        && prevProps.chartType === nextProps.chartType) {
        return true;
    }
    return false;
})